import { alpha, Box, useTheme } from '@mui/material'
import React, { ReactNode, useSyncExternalStore } from 'react'
import { Navigate, Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/hooks/use-auth'
import {
    getAuthLayoutRedirectSuppressedSnapshot,
    subscribeAuthLayoutGate,
} from '@/domain/auth/auth-layout-gate'
import {
    storefrontJoin,
    stripStorefrontPrefix,
    useStorefrontBasePath,
} from '@/utils/storefront-paths'

function AuthLayout({ children: _children }: { children?: ReactNode }) {

    const theme = useTheme()
    const navigate = useNavigate()
    const location = useLocation()
    const basePath = useStorefrontBasePath()
    const { isAuthenticated } = useAuth()
    const accountHref = storefrontJoin(basePath, '/my-account')
    const suppressRedirect = useSyncExternalStore(
        subscribeAuthLayoutGate,
        getAuthLayoutRedirectSuppressedSnapshot,
        () => false
    )
    // Register owns authenticated navigation (refresh → smart post-signup target).
    // AuthLayout must not steal that decision with a hard /my-account redirect.
    const isRegisterRoute = stripStorefrontPrefix(location.pathname) === '/register'
    const isLoginRoute = stripStorefrontPrefix(location.pathname) === '/login'
    const isOAuthFailureCallbackRoute =
        stripStorefrontPrefix(location.pathname) === '/auth/callback'
    const usesWarmAuthChrome = isLoginRoute || isOAuthFailureCallbackRoute
    const shouldRedirectAway =
        isAuthenticated && !suppressRedirect && !isRegisterRoute

    React.useEffect(() => {
        if (shouldRedirectAway) {
            navigate(accountHref, { replace: true })
        }
    }, [shouldRedirectAway, navigate, accountHref])


    return (
        shouldRedirectAway
            ? <Navigate to={accountHref} replace />
            : <Box
                className={'flexCenter flexColumn'}
                sx={{
                    minHeight: 'calc(100vh - 150px)',
                    // Login / OAuth failure own warm gradient page chrome via Login.module.css
                    ...(usesWarmAuthChrome
                        ? {
                              background: 'transparent',
                              width: '100%',
                              px: 0,
                              py: 0,
                          }
                        : null),
                    '& .authLayout': {
                        flex: 1,
                        px: { xs: '5%', sm: '10%' },
                        py: 4,
                        alignSelf: 'stretch',
                        alignItems: 'stretch',
                        gap: 2,
                        '& .headBlock': {
                            flex: 1,
                            '& .heading': {
                                fontSize: 'clamp(2rem, 20vw, 5rem)',
                                fontWeight: 'bold',
                                fontFamily: theme.typography.h1.fontFamily,
                                color: alpha(theme.palette.text.primary, 0.1),
                            },
                            '& .tagline': {
                                mt: 1,
                                pl: 1,
                                fontSize: 'clamp(0.85rem, 3vw, 1rem)',
                                color: theme.palette.text.secondary,
                            }
                        },
                        '& form': {
                            flex: 1,
                            columnGap: 1.5,
                            '& .Muibutton-root': {
                                mt: 2,
                            }
                        },
                        '& .bottomBlock': {
                            py: 2,
                            '& .link': {
                                columnGap: 1,
                                fontSize: theme.typography.pxToRem(13),
                                '& a': {
                                    fontWeight: 600,
                                    textDecoration: 'none',
                                    color: theme.palette.primary.main,
                                },
                                '& em': {
                                    color: theme.palette.text.secondary,

                                }
                            }
                        },
                    }
                }} >
                <Outlet />
            </ Box>
    )
}

export default AuthLayout
